import { useEffect, useState } from 'react'

import { clampDraft, daysInMonth } from './utils'
import type { DraftDate } from './types'

function dateToDraft(value: Date | null, defaultValue?: Date): DraftDate {
  // Без значения открываем не «сегодня», а переданный дефолт: для даты рождения
  // сегодняшнее число бессмысленно и сразу упирается в возрастную валидацию.
  const base = value ?? defaultValue ?? new Date()
  return { day: base.getDate(), month: base.getMonth(), year: base.getFullYear() }
}

/**
 * Черновой выбор даты, живёт до нажатия «Готово». Переинициализируется из value
 * каждый раз, когда шторка открывается, и всегда остаётся внутри `minDate`/`maxDate`:
 * запрещённую дату барабаны не отдают даже при кручении года в крайний.
 */
export function useDatePickerDraft(
  isOpen: boolean,
  value: Date | null,
  defaultValue: Date | undefined,
  minDate: Date,
  maxDate: Date,
) {
  const [draft, setDraft] = useState<DraftDate>(() =>
    clampDraft(dateToDraft(value, defaultValue), minDate, maxDate),
  )

  useEffect(() => {
    if (isOpen) setDraft(clampDraft(dateToDraft(value, defaultValue), minDate, maxDate))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const update = (next: (prev: DraftDate) => DraftDate) =>
    setDraft((prev) => clampDraft(next(prev), minDate, maxDate))

  const setDay = (day: number) => update((prev) => ({ ...prev, day }))

  const setMonth = (month: number) =>
    update((prev) => ({
      ...prev,
      month,
      day: Math.min(prev.day, daysInMonth(month, prev.year)),
    }))

  const setYear = (year: number) =>
    update((prev) => ({
      ...prev,
      year,
      day: Math.min(prev.day, daysInMonth(prev.month, year)),
    }))

  return { draft, setDay, setMonth, setYear }
}
